import * as React from 'react';

import { useResizeObserver } from '@gitbook/util-react-hooks';

type EditorWidthContextType = {
    subscribe: (onStoreChange: () => void) => () => void;
    getWidth: () => number;
};
const EditorWidthContext = React.createContext<EditorWidthContextType | null>(null);

/**
 * Set a boundary to measure the editor.
 */
export function EditorWidthLayout(props: React.ComponentProps<'div'>) {
    const listenersRef = React.useRef<Set<() => void>>(new Set());
    const widthRef = React.useRef<number>(0);
    const value = React.useMemo<EditorWidthContextType>(
        () => ({
            subscribe: (onStoreChange) => {
                listenersRef.current.add(onStoreChange);
                return () => {
                    listenersRef.current.delete(onStoreChange);
                };
            },
            getWidth: () => widthRef.current,
        }),
        []
    );
    const ref = useResizeObserver((entry) => {
        widthRef.current = entry.contentRect.width;
        listenersRef.current.forEach((cb) => cb());
    });
    return (
        <EditorWidthContext.Provider value={value}>
            <div ref={ref} {...props} />
        </EditorWidthContext.Provider>
    );
}

/**
 * Get the editor width from the closest `EditorWidthLayout` in the tree.
 */
export function useEditorWidth() {
    const ctx = React.useContext(EditorWidthContext);
    if (!ctx) {
        throw new Error(`Must be used in a ContentEditor`);
    }
    return React.useSyncExternalStore(ctx.subscribe, ctx.getWidth);
}
